import React from 'react';
import MetricScatterChart from './MetricScatterChart';

// Prefer min/max/sd from the API response; fall back to computing from data points
const getStats = (d) => {
  if (!d) return { min: null, max: null, sd: null };

  // Use API-provided values when present
  const apiMin = (d.min != null && !isNaN(d.min)) ? d.min : null;
  const apiMax = (d.max != null && !isNaN(d.max)) ? d.max : null;
  const apiSd  = (d.sd  != null && !isNaN(d.sd))  ? d.sd  : null;

  if (apiMin !== null && apiMax !== null && apiSd !== null) {
    return { min: apiMin, max: apiMax, sd: apiSd };
  }

  // Fallback: compute from data array
  if (!d.data || d.data.length === 0) return { min: null, max: null, sd: null };
  const values = d.data.map(item => item.value).filter(v => v != null && !isNaN(v));
  if (values.length === 0) return { min: null, max: null, sd: null };
  const min = Math.min(...values);
  const max = Math.max(...values);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((sum, v) => sum + Math.pow(v - mean, 2), 0) / values.length;
  return { min, max, sd: Math.sqrt(variance) };
};

const StatChartTab = ({
  chartTitleTh,
  chartTitleEn,
  yAxisLabel,
  data,
  loading,
  color,
  avgSuffix,
  lang,
}) => {
  const l = (th, en) => (lang === 'th' ? th : en);
  const suffix = avgSuffix || '';

  const formatNum = (val) => {
    if (val == null || isNaN(val)) return '-';
    return val.toFixed(2) + suffix;
  };

  const formatAvg = (d) => {
    if (!d || d.avg == null || isNaN(d.avg)) return '-';
    return d.avg.toFixed(2) + suffix;
  };

  const getChartPoints = (d) => (d && d.data) ? d.data.map(item => item.value) : [];
  const getChartLabels = (d) => (d && d.data) ? d.data.map(item => item.name) : [];

  const stats = getStats(data);

  const renderLoading = () => (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: 520,
      flexDirection: 'column',
      color: '#666'
    }}>
      <i className="fas fa-spinner fa-spin fa-3x" style={{ marginBottom: 16 }} />
      <span>{l('กำลังโหลดข้อมูล...', 'Loading...')}</span>
    </div>
  );

  const STAT_COLORS = {
    avg: '#1677FF',
    min: '#52C41A',
    max: '#FA541C',
    sd:  '#722ED1',
  };

  const makeStatBadge = (accentColor) => ({
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '4px 18px',
    borderRadius: 6,
    border: `1px solid ${accentColor}40`,
    backgroundColor: `${accentColor}0F`,
    minWidth: 90,
  });

  const makeStatLabel = (accentColor) => ({
    fontSize: 11,
    color: accentColor,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  });

  const makeStatValue = (accentColor) => ({
    fontSize: 15,
    fontWeight: 700,
    color: accentColor,
    marginTop: 2,
  });

  return (
    <React.Fragment>
      <div className="chart-head">
        <h3 style={{ marginTop: 0 }}>{l(chartTitleTh, chartTitleEn)}</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
          <div style={makeStatBadge(STAT_COLORS.avg)}>
            <span style={makeStatLabel(STAT_COLORS.avg)}>{l('ค่าเฉลี่ย', 'Average')}</span>
            <span style={makeStatValue(STAT_COLORS.avg)}>{formatAvg(data)}</span>
          </div>
          <div style={makeStatBadge(STAT_COLORS.min)}>
            <span style={makeStatLabel(STAT_COLORS.min)}>Min</span>
            <span style={makeStatValue(STAT_COLORS.min)}>{formatNum(stats.min)}</span>
          </div>
          <div style={makeStatBadge(STAT_COLORS.max)}>
            <span style={makeStatLabel(STAT_COLORS.max)}>Max</span>
            <span style={makeStatValue(STAT_COLORS.max)}>{formatNum(stats.max)}</span>
          </div>
          <div style={makeStatBadge(STAT_COLORS.sd)}>
            <span style={makeStatLabel(STAT_COLORS.sd)}>SD</span>
            <span style={makeStatValue(STAT_COLORS.sd)}>{formatNum(stats.sd)}</span>
          </div>
        </div>
      </div>
      {loading ? renderLoading() : (
        <MetricScatterChart
          title=""
          yAxisLabel={yAxisLabel}
          xAxisLabel={l('อาคาร', 'Building')}
          averageText=""
          points={getChartPoints(data)}
          labels={getChartLabels(data)}
          color={color}
          height={520}
          hideInlineTitle
          withLine={false}
        />
      )}
    </React.Fragment>
  );
};

export default StatChartTab;



// WEBPACK FOOTER //
// ./src/components/charts/StatChartTab.js